import { Skeleton } from '../ui/skeleton';
import { APP_I18N } from '@/client/confs/app';
import useLocale from '@/client/hooks/use-locale';
import { api } from '@/client/utils/api';
import { useApiTranslation } from '@/libs/react-sdk/api/use-api-translation';
import useInViewport from 'ahooks/lib/useInViewport';
import { forwardRef, HTMLAttributes, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ApiTranslationProps = PropsWithChildren & HTMLAttributes<HTMLSpanElement>;

let globalTranslationReady = false;
let globalReadyTimer: ReturnType<typeof setTimeout> | undefined;
if (!globalReadyTimer) {
  globalReadyTimer = setTimeout(() => { globalTranslationReady = true; }, 2000);
}

export const ApiTranslation = forwardRef<HTMLSpanElement, ApiTranslationProps>(
  ({ children, ...attrs }: ApiTranslationProps, ref) => {
    const elementRef = useRef<HTMLSpanElement | null>(null);
    const [inViewport] = useInViewport(elementRef);
    const [ready, setReady] = useState(globalTranslationReady);
    useEffect(() => {
      if (ready) return;
      const id = setTimeout(() => setReady(true), 2000);
      return () => clearTimeout(id);
    }, [ready]);
    useEffect(() => {
      if (typeof ref === 'function') {
        ref(elementRef.current);
      } else if (ref) {
        ref.current = elementRef.current;
      }
    }, [ref]);
    const { t, i18n } = useTranslation();
    const { locale, defaultLocale } = useLocale();
    const text = typeof children === 'string' ? children.trim() : '';
    const hasTr = text.length > 0 && i18n.exists(text.toLocaleLowerCase());
    const enabled = ready && text.length > 0 && locale !== defaultLocale && !!inViewport && !hasTr;
    const { data, isLoading } = useApiTranslation(
      api,
      {
        text,
        sourceLang: defaultLocale,
        targetLang: locale,
      },
      { enabled, queryKey: [APP_I18N.onlineVersion] }
    );
    if (enabled && isLoading) return <Skeleton className="w-full min-w-15 h-[1em]" />;
    return (
      <span ref={elementRef} {...attrs}>
        {hasTr ? t(text.toLowerCase()) : data?.translatedText || children}
      </span>
    );
  }
);

ApiTranslation.displayName = 'ApiTranslation';
